import { getListItems } from '../graph/lists';
import { executeListOps } from '../graph/listOps';
import { LISTS, READ_CACHE_MS } from './listNames';
import { partyOf, type Party } from '../audit/requirements';
import { planLongDistanceRecompute } from '../audit/clockCredit';
import { getPublishedTemplates } from './templates';

/**
 * StoreAuditStatus readback: one row per store × template × responsible
 * party (DM / Auditor / Store team clock), written by the
 * sync engine on submission and recalculated daily by the Phase 4 flow.
 * The app treats the ROW DATES as truth and computes display status live
 * (src/audit/windows.ts) — WindowStatus in the row is for flows/analytics.
 */

export interface StoreStatusRow {
  id: number;
  storeId: number;
  templateId: number;
  lastCompletedDate: string | null;
  nextDueDate: string | null;
  windowStatus: string;
  /** Responsible-party clock this row is (blank in SharePoint = the DM's). */
  party: Party;
  /** Flow 1's bookkeeping: the last status an email went out for (due_soon | overdue | escalated | current). */
  lastNotifiedStatus?: string;
  /** Flow 1's bookkeeping: when the last overdue reminder went out. */
  lastOverdueReminderAt?: string | null;
}

interface StatusFields {
  StoreId: number;
  TemplateId: number;
  LastCompletedDate: string;
  NextDueDate: string;
  WindowStatus: string;
  Party: string;
  LastNotifiedStatus: string;
  LastOverdueReminderAt: string;
}

function toRow(id: string, f: Partial<StatusFields>): StoreStatusRow {
  return {
    id: Number(id),
    storeId: f.StoreId ?? 0,
    templateId: f.TemplateId ?? 0,
    lastCompletedDate: f.LastCompletedDate ?? null,
    nextDueDate: f.NextDueDate ?? null,
    windowStatus: f.WindowStatus ?? '',
    party: partyOf(f.Party),
    lastNotifiedStatus: f.LastNotifiedStatus ?? '',
    lastOverdueReminderAt: f.LastOverdueReminderAt ?? null,
  };
}

/** All status rows (≤ stores × templates — small; unfiltered fetch). */
export async function getAllStoreStatuses(): Promise<StoreStatusRow[]> {
  const items = await getListItems<StatusFields>(LISTS.storeAuditStatus, { cacheMs: READ_CACHE_MS });
  return items.map((i) => toRow(i.id, i.fields));
}

/** Status rows for one store (StoreId indexed). */
export async function getStoreStatusesForStore(
  storeId: number,
): Promise<StoreStatusRow[]> {
  const items = await getListItems<StatusFields>(LISTS.storeAuditStatus, {
    filter: `fields/StoreId eq ${storeId}`,
  });
  return items.map((i) => toRow(i.id, i.fields));
}

/**
 * After a store's Long distance flag changes (Admin → Stores): move each of
 * its clocks to last completed + the window that now applies (owner ruling
 * 2026-10-20: right away, not at the next audit). Returns how many due
 * dates changed; throws if any write failed (the caller offers a retry —
 * the plan is recomputed from fresh reads, so a retry is safe).
 */
export async function recomputeLongDistanceClocks(storeId: number, longDistance: boolean): Promise<number> {
  const [clocks, templates] = await Promise.all([getStoreStatusesForStore(storeId), getPublishedTemplates()]);
  const plan = planLongDistanceRecompute({
    clocks: clocks.map((c) => ({
      id: String(c.id),
      templateId: c.templateId,
      party: c.party,
      lastCompleted: c.lastCompletedDate,
      nextDue: c.nextDueDate,
    })),
    templates,
    longDistance,
  });
  if (plan.length === 0) return 0;
  const responses = await executeListOps(
    plan.map((p, i) => ({
      id: `ld-${i}`,
      method: 'PATCH' as const,
      list: LISTS.storeAuditStatus,
      itemId: p.rowId,
      fields: { NextDueDate: p.nextDue },
    })),
  );
  const failed = [...responses.values()].filter((r) => r.status < 200 || r.status >= 300).length;
  if (failed > 0) throw new Error(`${failed} of ${plan.length} due dates weren’t recalculated.`);
  return plan.length;
}
